<template>
  <div class="store-credit-tiers" :class="{ 'store-credit-tiers--tooltip': isTooltip }">
    <div v-if="!isTooltip" class="store-credit-tiers__header">
      <img class="store-credit-tiers__icon" alt="Loyalty Badge" src="https://jiffy.imgix.net/gen/loyalty-badge.png" />
      <p class="store-credit-tiers__text">
        <span v-if="expectedCredits > 0">
          You'll earn <strong>{{ n(expectedCredits, "currency") }} credit</strong> on Transfers!
        </span>
        <span v-else>
          Earn credits on Transfers!
        </span>
      </p>
    </div>
    <div class="store-credit-tiers__progress">
      <p v-if="nextTier" class="store-credit-tiers__progress-text">
        You're <strong>{{ n(amountToNextTier, "currency") }}</strong> away from
        <strong>{{ nextTier.percentage }}%</strong> store
        credit.
      </p>
      <p v-else class="store-credit-tiers__progress-text">You’ve earned <strong>7.5%</strong> store credit!</p>
      <Tooltip v-if="!isTooltip" trigger="click" position="top-right">
        <template #target>
          <UseSvg icon="info-circle-bold" class="store-credit-tiers__info-icon" />
        </template>
        <template #content>
          <div class="store-credit-tiers__tooltip">
            Earn up to 7.5% back on every purchase!
            <a href="/transfer-rewards-terms-conditions">Terms and Conditions</a>
          </div>
        </template>
      </Tooltip>
    </div>
    <div class="store-credit-tiers__tiers">
      <div
        v-for="(tier, index) in filteredTiers"
        :key="index" class="store-credit-tiers__tier"
        :class="{ 'store-credit-tiers__tier--active': currentTier.percentage === tier.percentage }"
      >
        <span class="store-credit-tiers__tier-rate">{{ tier.percentage }}%</span>
        <span class="store-credit-tiers__tier-range">{{ tier.range }}</span>
      </div>
    </div>
    <a v-if="isTooltip" href="/transfer-rewards-terms-conditions" class="store-credit-tiers__link">
      Terms and Conditions
    </a>
  </div>
</template>

<script>
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { n } from "components/jiffy/vue-cart/helpers/currency";
import Tooltip from "common/vue/components/Tooltip.vue";

export default {
  name: "StoreCreditTiers",
  components: { UseSvg, Tooltip },
  props: {
    expectedCredits: {
      type: [String, Number],
      default: null,
    },
    totalAmount: {
      type: [String, Number],
      default: null,
    },
    currentTier: {
      type: Object,
      default: null,
    },
    tiers: {
      type: Array,
      default: () => [],
    },
    isTooltip: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return { n };
  },
  computed: {
    filteredTiers() {
      return this.tiers.filter((tier) => tier.percentage > 0);
    },
    nextTier() {
      return this.tiers.find((tier) => this.totalAmount > 0 && this.totalAmount < tier.min) || null;
    },
    amountToNextTier() {
      if (this.nextTier) {
        return this.nextTier.min - this.totalAmount;
      }
      return 0;
    },
  },
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.store-credit-tiers {
  $this: &;

  max-width: 100%;
  padding: 16px;
  border-radius: 4px;
  background-color: $light-aqua;
  margin-top: 8px;

  @include with-desktop {
    max-width: 320px;
  }


  &--tooltip {
    width: 325px;
    margin-top: 0;
    background-color: $white;
    box-shadow: 0px 15px 20px 0px rgba($black, 0.04);
    border: 1px solid $crafterpay-gray-modern-200;

    @include with-desktop {
      max-width: 325px;
    }

    #{$this}__tier {
      border: 1px solid $crafterpay-gray-modern-200;

      &--active {
      border-color: $crafterpay-gray-modern-800;
    }
    }
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
  }

  &__icon {
    width: 24px;
    height: 24px;
  }

  &__text {
    font-family: $font-family-secondary;
    font-size: 14px;
    font-weight: 700;
    line-height: 14px;
    text-align: left;
    margin: 0;
  }

  &__progress {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;
  }

  &__progress-text {
    font-family: $font-family-secondary;
    font-size: 13px;
    font-weight: 400;
    line-height: 14px;
    text-align: center;
    margin: 0;
  }

  &__info-icon {
    width: 20px;
    height: 20px;
    margin-left: 4px;
    cursor: pointer;
  }

  &__tiers {
    display: flex;
    justify-content: center;
    gap: 8px;
  }

  &__tier {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px;
    background-color: $white;
    border-radius: 4px;
    gap: 5px;

    &--active {
      border: 1px solid $crafterpay-gray-modern-800;
      box-shadow: 0px 0px 5px 0px rgba($black, 0.7);
    }
  }


  &__tier-rate {
    height: 20px;
    width: 58px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $store-credit-tooltip-green;
    font-family: $font-family-secondary;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 700;
    color: $white;
    margin: 0;
  }

  &__tier-range {
    font-family: $font-family-secondary;
    font-size: 13px;
    font-weight: 700;
    line-height: 16px;
    text-align: center;
    margin: 0;
  }

  &__link {
    display: block;
    text-align: center;
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
    margin-top: 8px;
    margin-right: 5px;
    text-decoration: underline;
  }

  &__tooltip {
    font-size: 13px;
    line-height: 16px;
    font-weight: 400;
    width: 260px;
    background-color: $white;
    padding: 8px;
    border-radius: 4px;
    box-shadow: 0px 15px 20px 0px rgba($black, 0.04);
    border: 1px solid $crafterpay-gray-modern-200;
  }
}
</style>
